import { z } from 'zod';
import { INVENTORY_CATEGORIES, INVENTORY_UNITS } from '../constants.js';
import { nonEmptyTrimmed, optionalTrimmed, paginated, uuidSchema } from './common.js';
export const createInventoryItemSchema = z.object({
    name: nonEmptyTrimmed(120),
    category: z.enum(INVENTORY_CATEGORIES).default('other'),
    materialId: uuidSchema.optional(),
    brandId: uuidSchema.optional(),
    unit: z.enum(INVENTORY_UNITS).default('sqft'),
    initialQty: z.number().min(0),
    reorderPoint: z.number().min(0),
    costCentsPerUnit: z.number().int().min(0).optional(),
    priceCentsPerUnit: z.number().int().min(0).optional(),
    sku: optionalTrimmed(60),
    location: optionalTrimmed(120),
    notes: optionalTrimmed(2000),
    // §12: optional color-reference swatch (a media_assets id, kind
    // 'material_swatch') the AI mockup uses to reproduce this material's exact
    // color and finish.
    swatchMediaId: uuidSchema.optional(),
});
export const updateInventoryItemSchema = z.object({
    name: nonEmptyTrimmed(120).optional(),
    category: z.enum(INVENTORY_CATEGORIES).optional(),
    materialId: uuidSchema.nullable().optional(),
    brandId: uuidSchema.nullable().optional(),
    unit: z.enum(INVENTORY_UNITS).optional(),
    reorderPoint: z.number().min(0).optional(),
    costCentsPerUnit: z.number().int().min(0).optional(),
    priceCentsPerUnit: z.number().int().min(0).optional(),
    sku: optionalTrimmed(60),
    location: optionalTrimmed(120),
    notes: optionalTrimmed(2000),
    isActive: z.boolean().optional(),
    // Nullable so the swatch can be cleared as well as set/replaced.
    swatchMediaId: uuidSchema.nullable().optional(),
});
export const adjustInventorySchema = z.object({
    delta: z.number().refine((v) => v !== 0, 'delta must not be zero'),
    reason: z.enum(['purchase', 'adjustment', 'shrinkage']),
    note: optionalTrimmed(500),
});
export const inventoryItemSchema = z.object({
    id: uuidSchema,
    name: z.string(),
    sku: z.string().nullable(),
    category: z.enum(INVENTORY_CATEGORIES),
    materialId: uuidSchema.nullable(),
    brandId: uuidSchema.nullable(),
    unit: z.enum(INVENTORY_UNITS),
    qtyOnHand: z.number(),
    qtyReserved: z.number(),
    reorderPoint: z.number(),
    costCentsPerUnit: z.number(),
    priceCentsPerUnit: z.number().int().nonnegative(),
    location: z.string(),
    notes: z.string(),
    isActive: z.boolean(),
    status: z.enum(['ok', 'low', 'out']),
    updatedAt: z.string(),
    // §12: color-reference swatch. swatchMediaId is the raw link; swatchUrl /
    // swatchThumbUrl are freshly signed read URLs (null when no swatch is set).
    swatchMediaId: uuidSchema.nullable(),
    swatchUrl: z.string().nullable(),
    swatchThumbUrl: z.string().nullable(),
});
export const inventoryListResponseSchema = paginated(inventoryItemSchema);
export const inventoryMovementSchema = z.object({
    id: uuidSchema,
    delta: z.number(),
    reason: z.string(),
    refJobId: uuidSchema.nullable(),
    note: z.string(),
    actorId: uuidSchema.nullable(),
    createdAt: z.string(),
});
export const availabilityResponseSchema = z.record(z.string(), z.array(z.object({ itemId: uuidSchema, name: z.string(), onHand: z.number(), unit: z.enum(INVENTORY_UNITS), reorderPoint: z.number() })));
//# sourceMappingURL=inventory.js.map